import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, FolderOpen, FileText, File, FileImage, FileSpreadsheet, Pencil, Trash2, Clock, Download, Tag } from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  { value: 'contract', label: 'Contract', color: 'bg-blue-100 text-blue-700' },
  { value: 'invoice', label: 'Invoice', color: 'bg-green-100 text-green-700' },
  { value: 'report', label: 'Report', color: 'bg-purple-100 text-purple-700' },
  { value: 'policy', label: 'Policy', color: 'bg-amber-100 text-amber-700' },
  { value: 'template', label: 'Template', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-600' },
];
function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }
function fileIcon(type) { if (!type) return FileText; if (type.includes('image')) return FileImage; if (type.includes('sheet') || type.includes('csv')) return FileSpreadsheet; return File; }

function DocModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ name: '', description: '', category: 'other', file_url: '', file_type: '', file_size: '', tags: '' });
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useState(null);

  useEffect(() => {
    if (editing) setForm({ name: editing.name || '', description: editing.description || '', category: editing.category || 'other', file_url: editing.file_url || '', file_type: editing.file_type || '', file_size: editing.file_size || '', tags: (editing.tags || []).join(', ') });
    else setForm({ name: '', description: '', category: 'other', file_url: '', file_type: '', file_size: '', tags: '' });
    setUploadFile(null);
  }, [editing, open]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50_000_000) { toast.error('Max 50MB'); return; }
    setUploadFile(file);
    setForm(p => ({
      ...p,
      name: p.name || file.name.replace(/\.[^.]+$/, ''),
      file_type: file.name.split('.').pop(),
      file_size: `${(file.size / 1024).toFixed(0)} KB`,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      let file_url = form.file_url;

      // Upload file if selected
      if (uploadFile) {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(uploadFile);
        });
        const uploadRes = await axios.post(`${API}/api/media`, {
          title: form.name,
          media_type: 'image',
          description: `Document Vault: ${form.name}`,
          assignment: 'document_vault',
          data_base64: base64,
          file_name: uploadFile.name,
        });
        file_url = uploadRes.data.file_url || '';
      }

      const payload = { ...form, file_url, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
      const { data } = editing
        ? await axios.put(`${API}/api/operations/documents/${editing.id}`, payload)
        : await axios.post(`${API}/api/operations/documents`, payload);
      toast.success(editing ? 'Updated' : 'Document added');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><FolderOpen className="h-5 w-5 text-amber-500" />{editing ? 'Edit' : 'Add'} Document</DialogTitle><DialogDescription>Upload and organize business documents</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          {/* File Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-amber-400 transition-colors"
            onClick={() => document.getElementById('doc-file-input')?.click()}>
            <input id="doc-file-input" type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.mp4,.zip" onChange={handleFileSelect} />
            {uploadFile ? (
              <div className="flex items-center justify-center gap-2">
                <File className="h-5 w-5 text-amber-500" />
                <span className="text-sm font-medium">{uploadFile.name}</span>
                <span className="text-xs text-gray-400">({(uploadFile.size / 1024).toFixed(0)} KB)</span>
              </div>
            ) : (
              <div>
                <FolderOpen className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                <p className="text-sm text-gray-500">Click to upload a file (max 50MB)</p>
                <p className="text-[10px] text-gray-400">PDF, DOC, XLS, PPT, Images, Videos</p>
              </div>
            )}
          </div>

          <div><Label>Document Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} data-testid="doc-name" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}><SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>File Type</Label><Input value={form.file_type} onChange={e => setForm(p => ({...p, file_type: e.target.value}))} placeholder="pdf, docx..." /></div>
          </div>
          {!uploadFile && <div><Label>Or File URL</Label><Input value={form.file_url} onChange={e => setForm(p => ({...p, file_url: e.target.value}))} placeholder="https://..." /></div>}
          <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="brochure, specs, catalog" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-doc-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Upload & Add'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

export default function DocumentVaultPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchDocs = useCallback(async () => {
    try {
      const params = {};
      if (filterCat !== 'all') params.category = filterCat;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/api/operations/documents`, { params,  });
      setDocs(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search, filterCat]);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleSaved = (d, isUpdate) => { if (isUpdate) setDocs(p => p.map(x => x.id === d.id ? d : x)); else setDocs(p => [d, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete document?')) return; try { await axios.delete(`${API}/api/operations/documents/${id}`); setDocs(p => p.filter(d => d.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="document-vault-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><FolderOpen className="h-5 w-5 text-amber-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Document Vault<PageInfoTooltip pageKey="documents" /></h1><p className="text-xs text-gray-400">{docs.length} documents</p></div></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-36 h-9 text-sm" data-testid="doc-search" /></div>
          <Select value={filterCat} onValueChange={setFilterCat}><SelectTrigger className="w-28 h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-amber-600 hover:bg-amber-700 h-9 gap-1.5" data-testid="new-doc-btn"><Plus className="h-4 w-4" /> Add Document</Button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" /></div> : docs.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No documents</h3><p className="text-sm text-gray-400 mb-4">Start organizing your files</p><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-amber-600 hover:bg-amber-700 gap-1.5"><Plus className="h-4 w-4" /> Add Document</Button></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{docs.map(d => { const catInfo = CATEGORIES.find(c => c.value === d.category) || CATEGORIES[5]; const FIcon = fileIcon(d.file_type); return (
          <Card key={d.id} className="border-gray-200 hover:shadow-sm transition-shadow" data-testid={`doc-card-${d.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><FIcon className="h-5 w-5 text-gray-500" /></div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900 line-clamp-1">{d.name}</h3>
                  <div className="flex items-center gap-1.5 mt-0.5"><Badge className={`${catInfo.color} text-[9px]`}>{catInfo.label}</Badge>{d.file_type && <span className="text-[10px] text-gray-400">{d.file_type}</span>}</div>
                </div>
              </div>
              {d.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{d.description}</p>}
              {d.tags && d.tags.length > 0 && <div className="flex gap-1 mt-2 flex-wrap">{d.tags.map((t, i) => <Badge key={i} variant="outline" className="text-[9px]">{t}</Badge>)}</div>}
              <div className="flex items-center justify-between mt-3">
                <span className="text-[10px] text-gray-400">{d.uploaded_by} · {timeAgo(d.created_at)}</span>
                <div className="flex gap-1">
                  {d.file_url && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => window.open(d.file_url, '_blank')}><Download className="h-3 w-3 text-gray-400" /></Button>}
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditing(d); setModalOpen(true); }}><Pencil className="h-3 w-3 text-gray-400" /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(d.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>); })}</div>
      )}
      <DocModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
    </div>
  );
}
